/**
 * Whether an org is designated for E2E testing, per `auth.organisation.Meta`.
 *
 * Runtime-agnostic on purpose: web (`E2eFlagOverridesContext`), tRPC
 * (`orgContext.isFeatureFlagEnabled`) and external-api all gate the
 * seeded-flag override on this, and they must agree. It lives at the package
 * root rather than under `src/server/` so the browser can import it without
 * pulling in `dd-trace`, and inside this package rather than
 * `@risksmart-app/shared` so no new workspace dependency has to be inlined
 * into the service containers (ENG-540).
 *
 * ## Accepts the string `'true'` — deliberately, not defensively (ENG-619)
 *
 * The column is JSONB and **every seeded e2e org holds the string**, not a
 * boolean: `api-stack/hasura/seeds/default/1678186528595_TestOrgUser.sql`
 * writes `'{"isE2eTestOrg":"true"}'::jsonb` in all 33 places. A strict
 * `=== true` therefore reported `false` for every real e2e org, silently
 * disabling the override so seeded flags were ignored and every flag-gated
 * spec failed against the live provider's registry defaults.
 *
 * `'true'` is accepted for that reason alone. Anything else truthy —
 * `'yes'`, `1`, `'TRUE'` — is still rejected: this is a two-value contract
 * with a known string encoding, not a general coercion. The narrowness is the
 * point; widening it to `Boolean(meta.isE2eTestOrg)` would let a typo enable
 * override mode on a customer org.
 *
 * Never throws — a malformed or absent `Meta` is simply "not an e2e org".
 */
export const isE2eTestOrg = (meta: unknown): boolean => {
  if (typeof meta !== 'object' || meta === null || !('isE2eTestOrg' in meta)) {
    return false;
  }

  return meta.isE2eTestOrg === true || meta.isE2eTestOrg === 'true';
};
